import { useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { API_URL, queryDelete, queryGet } from './fetch';
import { IEvent } from './availabilities';

export interface Calendar {
  id: string;
  accountId: string;
  organizationId: string;
  calendarAccountId: string;
  name: string;
  provider: string;
  profileId: string;
  profileName: string;
  isReadonly: boolean;
  isDeleted: boolean;
  isPrimary: boolean;
  isVisible: boolean;
  isDisconnected: boolean;
  integratedConferencingServiceAvailable: boolean;
  cronofyConferencingAvailable: boolean;
}

export interface ConferencingProfile {
  providerName: string;
  profileId: string;
  profileName: string;
  profileConnected: boolean;
  relinkUrl?: string;
}

export const useGoToCalendarAuthUrl = (organizationId?: string) => {
  return useCallback(() => {
    // We use "window.open" to be able to close the window after the user has authorized the calendar
    // "window.close" can only be called on windows that were opened by a script
    window.open(`${API_URL}/integrations/cronofy/connect`, '_blank');
  }, []);
};

export const useConferencingAuthorizeUrl = (provider?: string) => {
  return useQuery<{ redirectUrl: string }>(
    ['conferencing', 'oauth', 'authorizeUrl', provider],
    queryGet(`/v2/conferencing/oauth/authorizeUrl`, {
      provider,
    }),
    {
      staleTime: Infinity,
    },
  );
};

export const useMyConferencingProviders = () => {
  return useQuery<ConferencingProfile[]>(
    ['me', 'conferencings'],
    queryGet(`/v2/me/conferencings`),
  );
};

export const useOrganizationCalendars = (organizationId?: string) => {
  const queryClient = useQueryClient();
  return useQuery<Calendar[]>(
    ['organizations', organizationId, 'calendars'],
    queryGet(`/v2/organizations/${organizationId}/calendars`),
    {
      enabled: !!organizationId,
      onSuccess: (calendars: Calendar[]) => {
        calendars.forEach((cal) => {
          queryClient.setQueryData(['calendars', cal.id], cal);
        });
      },
    },
  );
};

export const useAccountCalendars = (accountId?: string) => {
  const queryClient = useQueryClient();
  return useQuery<Calendar[]>(
    ['accounts', accountId, 'calendars'],
    queryGet(`/v2/accounts/${accountId}/calendars`),
    {
      enabled: !!accountId,
      onSuccess: (calendars: Calendar[]) => {
        calendars.forEach((cal) => {
          queryClient.setQueryData(['calendars', cal.id], cal);
        });
      },
    },
  );
};

export const useCalendar = (id?: string) => {
  return useQuery<Calendar>(
    ['calendars', id],
    queryGet(`/v2/calendars/${id}`),
    { enabled: !!id },
  );
};

export interface CalendarEvent extends IEvent {
  participationId?: string;
  fromTandemz?: boolean;
  start: Date;
  end: Date;
  title?: string;
}

export const useGetCalendarEvents = () => {
  const queryClient = useQueryClient();
  return useCallback(
    async (calendarId: string, params: any) => {
      const queryKey = ['calendars', calendarId, 'events', params];
      if (queryClient.getQueryData(queryKey)) {
        return queryClient.getQueryData<CalendarEvent[]>(queryKey);
      }
      const res = await queryGet<CalendarEvent[]>(
        `/v2/calendars/${calendarId}/events`,
        params,
      )();
      queryClient.setQueryData(queryKey, res);
      return res;
    },
    [queryClient],
  );
};
